import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import useTheme from '@mui/material/styles/useTheme';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DataItem } from '~components/DataItem';
import ControlledCheckbox from '~components/Form/ControlledCheckbox';
import ControlledNumberField from '~components/Form/ControlledNumberField';
import ControlledSwitch from '~components/Form/ControlledSwitch';
import SectionCard from '~components/SectionCard';
import { updateLeadsListById } from '~pages/CampaignManagement/api';
import { LeadList, ListPriority, UpdateLeadListGeneralSettings } from '~pages/CampaignManagement/domain';
import MoveLeadsListForm from '~pages/CampaignManagement/LeadListDetails/LeadListSettings/MoveLeadsListForm';
import { useAuth } from '~providers/AuthProvider';
import { AccessScope } from '~providers/AuthProvider/domain';
import { useNotification } from '~providers/NotificationProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';
import { deepCompare } from '~utils/Functions';

const enum EditType {
  General = 'general',
}

interface LeadListSettingsProps {
  leadList: LeadList;
  triggerLeadListRefresh: () => Promise<void>;
}

const DATE_FORMAT = 'FFF';

const formatDate = (date: string, formatString: string) => {
  if (date) {
    return DateTime.fromISO(date).toFormat(formatString);
  }
  return '-';
};

interface Form {
  isActive: boolean;
  name: string;
  description: string;
  priority: number;
  setStartEndTime: boolean;
  startTime: string | null;
  endTime: string | null;
}

const LeadListSettings = ({ leadList, triggerLeadListRefresh }: LeadListSettingsProps) => {
  const theme = useTheme();
  const { hasScope } = useAuth();
  const { campaignId, listId } = useParams() as { campaignId: string; listId: string };
  const { pushNotification } = useNotification();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [edit, setEdit] = useState<EditType | undefined>(undefined);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [endTimeValidationError, setEndTimeValidationError] = useState<string | undefined>(undefined);
  const {
    formState: { errors },
    handleSubmit,
    reset,
    control,
    watch,
  } = useForm<Form>({
    defaultValues: {
      isActive: false,
      name: '',
      description: '',
      priority: 0,
      setStartEndTime: false,
      startTime: null,
      endTime: null,
    },
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });
  const disablePerformAction =
    leadList &&
    deepCompare(leadList.isActive, watch('isActive')) &&
    deepCompare(leadList.name, watch('name')) &&
    deepCompare(leadList.description, watch('description')) &&
    deepCompare(leadList.priority, watch('priority')) &&
    deepCompare(Boolean(leadList.startTime) || Boolean(leadList.endTime), watch('setStartEndTime')) &&
    deepCompare(leadList.startTime || null, watch('startTime')) &&
    deepCompare(leadList.endTime || null, watch('endTime'));
  const [startTime, setStartEndTime] = watch(['startTime', 'setStartEndTime']);
  const fieldsDateToMinDate = useMemo(() => (startTime ? DateTime.fromISO(startTime) : undefined), [startTime]);

  // Handles reset and editable display
  useEffect(() => {
    if (edit === EditType.General) {
      reset({
        isActive: leadList.isActive,
        name: leadList.name,
        description: leadList.description,
        priority: leadList.priority,
        setStartEndTime: Boolean(leadList.startTime) || Boolean(leadList.endTime),
        startTime: leadList.startTime || null,
        endTime: leadList.endTime || null,
      });
    }

    // Reset form on close
    return () => {
      reset();
    };
  }, [edit]);

  // Manages optional start and end time properties within schema
  useEffect(() => {
    if (!setStartEndTime) {
      setEndTimeValidationError(undefined);
    }
  }, [setStartEndTime]);

  const toggleEdit = (value: EditType) => () => {
    setEdit((prev) => (prev === value ? undefined : value));
  };

  const onGeneralSettingsSubmit = handleSubmit(async (formData: Form) => {
    setSubmitting(true);
    const data: UpdateLeadListGeneralSettings = {
      ...formData,
      priority: formData.priority,
      startTime: formData.setStartEndTime ? formData.startTime || undefined : undefined,
      endTime: formData.setStartEndTime ? formData.endTime || undefined : undefined,
    };

    try {
      await updateLeadsListById(+campaignId, +listId, data);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);
      return;
    } finally {
      setSubmitting(false);
    }

    pushNotification('success', 'You have successfully updated the list.');
    await triggerLeadListRefresh();
    toggleEdit(EditType.General)();
  });

  return (
    <>
      <SectionCard
        title='General'
        onEdit={hasScope(AccessScope.CanUpdateLeadsListGeneralSettings) ? toggleEdit(EditType.General) : undefined}>
        {edit !== EditType.General && (
          <>
            <DataItem stacked={isExtraSmall} disableMargin title='Is active' value={leadList.isActive ? 'Yes' : 'No'} />
            <DataItem stacked={isExtraSmall} title='List name' value={leadList?.name} />
            <DataItem stacked={isExtraSmall} title='Description' value={leadList?.description} />
            <DataItem stacked={isExtraSmall} title='List priority' value={leadList?.priority} />
            <DataItem stacked={isExtraSmall} title='Start time' value={formatDate(leadList.startTime, DATE_FORMAT)} />
            <DataItem stacked={isExtraSmall} title='End time' value={formatDate(leadList.endTime, DATE_FORMAT)} />
          </>
        )}

        {edit === EditType.General && (
          <form onSubmit={onGeneralSettingsSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ControlledSwitch name='isActive' label='Is Active?' control={control} disabled={submitting} />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='name'
                  control={control}
                  rules={{ required: 'List name is required.' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant='outlined'
                      id='name'
                      label='List name'
                      disabled={submitting}
                      error={Boolean(errors.name)}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name='description'
                  control={control}
                  rules={{ required: 'List description is required.' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={4}
                      variant='outlined'
                      id='description'
                      label='List description'
                      disabled={submitting}
                      error={Boolean(errors.description)}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledNumberField
                  name='priority'
                  control={control}
                  rules={{
                    required: 'List priority is required.',
                    validate: (value) => {
                      if (value < ListPriority.Min || value > ListPriority.Max) {
                        return `List priority must be between ${1} - ${20}`;
                      }
                      return true;
                    },
                  }}
                  label='List Priority'
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.priority)}
                  helperText={errors.priority?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledCheckbox
                  name='setStartEndTime'
                  label='Set Start or End Time?'
                  control={control}
                  disabled={submitting}
                />
              </Grid>

              {setStartEndTime && (
                <>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name='startTime'
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          disableMaskedInput
                          componentsProps={{ actionBar: { actions: ['clear'] } }}
                          disabled={submitting}
                          label='Start time'
                          inputFormat='dd/MM/yyyy hh:mm a'
                          renderInput={(params) => <TextField {...params} fullWidth variant='outlined' />}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name='endTime'
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          disableMaskedInput
                          componentsProps={{ actionBar: { actions: ['clear'] } }}
                          minDate={fieldsDateToMinDate}
                          onError={(reason) => {
                            if (reason === 'minDate') {
                              setEndTimeValidationError('List End Time should not be before Start Time.');
                              return;
                            }
                            setEndTimeValidationError(undefined);
                          }}
                          disabled={submitting}
                          label='End time'
                          inputFormat='dd/MM/yyyy hh:mm a'
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              variant='outlined'
                              error={Boolean(endTimeValidationError)}
                              helperText={endTimeValidationError}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              <Grid sx={{ textAlign: 'right' }} item xs={12}>
                <Button onClick={toggleEdit(EditType.General)}>Cancel</Button>

                <LoadingButton
                  sx={{ marginLeft: 1 }}
                  type='submit'
                  variant='contained'
                  disableElevation
                  disabled={disablePerformAction}
                  loading={submitting}
                  color='primary'>
                  Update
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        )}
      </SectionCard>

      {hasScope(AccessScope.CanMoveLeadsList) && <MoveLeadsListForm campaignId={+campaignId} listId={+listId} />}
    </>
  );
};

export default LeadListSettings;
